import React from "react";
import { Link } from "react-router-dom";
import { Leaf, Package } from "lucide-react";
import { useCart } from "../lib/ctx";
import { money } from "../lib/api";
import { toast } from "sonner";
import InterestForm from "./InterestForm";

export function Placeholder({ category = "supplements" }) {
  return (
    <div className="oh-image-placeholder w-full rounded-t-xl overflow-hidden">
      {category === "honey" ? (
        <svg width="72" height="72" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2"><path d="M12 2l3 5h5l-4 4 2 6-6-3-6 3 2-6-4-4h5z" opacity=".5"/><circle cx="12" cy="12" r="4"/></svg>
      ) : <Leaf size={64} strokeWidth={1.2} />}
    </div>
  );
}

export default function ProductCard({ product, promoPercent = 10 }) {
  const { add } = useCart();
  const isHoney = product.category === "honey";
  const isDraft = product.status === "draft";
  const isComingSoon = product.status === "coming_soon";
  const hasImage = product.images && product.images.length > 0;
  const original = product.price;
  const price = product.discount_price ?? (promoPercent && !isDraft && !isComingSoon ? +(original * (1 - promoPercent / 100)).toFixed(2) : original);
  const inStock = (product.available_stock || 0) > 0;

  return (
    <div className={`${isHoney ? "oh-card-honey" : "oh-card"} rounded-xl overflow-hidden flex flex-col`} data-testid={`product-card-${product.slug}`}>
      <Link to={`/product/${product.slug}`} className="block relative">
        {hasImage ? (
          <img src={product.images[0]} alt={product.name} className="w-full aspect-square object-cover"/>
        ) : <Placeholder category={product.category}/>}
        {isComingSoon && (
          <div className="absolute top-3 right-3 oh-badge oh-badge-gold !text-[10px]">Coming Soon</div>
        )}
      </Link>
      <div className="p-5 flex-1 flex flex-col gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`oh-badge ${isHoney ? "oh-badge-gold" : "oh-badge-turq"}`}>{isHoney ? "Honey" : product.category === "herbal" ? "Herbal" : "Supplement"}</span>
          {product.featured && !isComingSoon && <span className="oh-badge oh-badge-lime">Bestseller</span>}
          {isDraft && <span className="oh-badge oh-badge-draft">Draft</span>}
        </div>
        <Link to={`/product/${product.slug}`} className="link-hover">
          <h3 className="font-serif text-xl leading-tight">{product.name}</h3>
        </Link>
        <p className="text-sm text-white/60 line-clamp-2">{product.short_description}</p>

        {isComingSoon ? (
          <div className="mt-auto pt-2 border-t border-[var(--oh-border)]">
            <InterestForm product={product} compact/>
          </div>
        ) : (
          <>
            <div className="mt-auto flex items-end justify-between gap-3">
              <div>
                {price !== original && original > 0 && <div className="text-xs strike">{money(original)}</div>}
                <div className={`font-mono text-lg ${isHoney ? "text-amber-300" : "text-lime-300"}`}>{original > 0 ? money(price) : "Set by admin"}</div>
              </div>
              <button
                disabled={isDraft || !inStock || original === 0}
                data-testid={`add-to-cart-${product.slug}`}
                onClick={() => { add({ ...product, discount_price: price !== original ? price : null }, 1); toast.success(`${product.name} added to basket`); }}
                className={`px-4 py-2 rounded-full text-xs font-semibold tracking-wider uppercase ${isHoney ? "oh-btn-gold" : "oh-btn-lime"} disabled:opacity-40`}>
                {isDraft ? "Draft" : !inStock ? "Sold Out" : "Add"}
              </button>
            </div>
            {inStock && !isDraft && (
              <div className="text-[11px] font-mono text-white/50 tracking-widest flex items-center gap-1.5"><Package size={12}/> {product.available_stock} in stock · {product.net_quantity || "—"}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
